import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const VenueForm = ({ venue, onSubmit, onCancel, isEditing = false }) => {
  const [formData, setFormData] = useState({
    name: '',
    address_line_1: '',
    address_line_2: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'IN',
    capacity: '',
    description: '',
    contact_phone: '',
    contact_email: '',
    amenities: []
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (venue) {
      setFormData({
        name: venue?.name || '',
        address_line_1: venue?.address_line_1 || '',
        address_line_2: venue?.address_line_2 || '',
        city: venue?.city || '',
        state: venue?.state || '',
        postal_code: venue?.postal_code || '',
        country: venue?.country || 'IN',
        capacity: venue?.capacity?.toString() || '',
        description: venue?.description || '',
        contact_phone: venue?.contact_phone || '',
        contact_email: venue?.contact_email || '',
        amenities: venue?.amenities || []
      });
    }
  }, [venue]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'Venue name is required';
    }

    if (!formData?.address_line_1?.trim()) {
      newErrors.address_line_1 = 'Address is required';
    }

    if (!formData?.city?.trim()) {
      newErrors.city = 'City is required';
    }

    if (!formData?.state?.trim()) {
      newErrors.state = 'State is required';
    }

    if (!formData?.postal_code?.trim()) {
      newErrors.postal_code = 'Postal code is required';
    }

    if (formData?.capacity && isNaN(parseInt(formData?.capacity))) {
      newErrors.capacity = 'Capacity must be a valid number';
    }

    if (formData?.contact_email && !formData?.contact_email?.includes('@')) {
      newErrors.contact_email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error for this field
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const submitData = {
        ...formData,
        capacity: formData?.capacity ? parseInt(formData?.capacity) : null
      };

      await onSubmit?.(submitData);
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAmenityToggle = (amenity) => {
    setFormData(prev => ({
      ...prev,
      amenities: prev?.amenities?.includes(amenity)
        ? prev?.amenities?.filter(a => a !== amenity)
        : [...(prev?.amenities || []), amenity]
    }));
  };

  const commonAmenities = [
    'Parking', 'WiFi', 'Air Conditioning', 'Sound System', 
    'Stage', 'Security', 'Catering', 'Green Room', 
    'Accessibility', 'Bar', 'Kitchen', 'Storage'
  ];

  return (
    <div className="flex flex-col h-full">
      <div className="p-6 border-b border-border">
        <h2 className="text-xl font-semibold text-foreground">
          {isEditing ? 'Edit Venue' : 'Create New Venue'}
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          {isEditing ? 'Update venue information' : 'Add comprehensive venue details with location and capacity information'}
        </p>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-foreground">Basic Information</h3>
            
            <Input
              label="Venue Name"
              placeholder="Enter venue name"
              value={formData?.name}
              onChange={(e) => handleInputChange('name', e?.target?.value)}
              error={errors?.name}
              required
            />

            <Input
              label="Description"
              placeholder="Brief description of the venue"
              value={formData?.description}
              onChange={(e) => handleInputChange('description', e?.target?.value)}
              multiline
              rows={3}
            />

            <Input
              label="Capacity"
              type="number"
              placeholder="Maximum capacity"
              value={formData?.capacity}
              onChange={(e) => handleInputChange('capacity', e?.target?.value)}
              error={errors?.capacity}
            />
          </div>

          {/* Address Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-foreground">Address</h3>
            
            <Input
              label="Address Line 1"
              placeholder="Street address"
              value={formData?.address_line_1}
              onChange={(e) => handleInputChange('address_line_1', e?.target?.value)}
              error={errors?.address_line_1}
              required
            />

            <Input
              label="Address Line 2"
              placeholder="Apartment, suite, building (optional)"
              value={formData?.address_line_2}
              onChange={(e) => handleInputChange('address_line_2', e?.target?.value)}
            />

            <div className="grid grid-cols-2 gap-4">
              <Input
                label="City"
                placeholder="City"
                value={formData?.city}
                onChange={(e) => handleInputChange('city', e?.target?.value)}
                error={errors?.city}
                required
              />

              <Input
                label="State"
                placeholder="State"
                value={formData?.state}
                onChange={(e) => handleInputChange('state', e?.target?.value)}
                error={errors?.state}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Postal Code"
                placeholder="Postal code"
                value={formData?.postal_code}
                onChange={(e) => handleInputChange('postal_code', e?.target?.value)}
                error={errors?.postal_code}
                required
              />

              <Input
                label="Country"
                placeholder="Country"
                value={formData?.country}
                onChange={(e) => handleInputChange('country', e?.target?.value)}
                disabled
              />
            </div>
          </div>

          {/* Contact Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-foreground">Contact Information</h3>
            
            <Input
              label="Contact Phone"
              placeholder="Phone number"
              value={formData?.contact_phone}
              onChange={(e) => handleInputChange('contact_phone', e?.target?.value)}
            />

            <Input
              label="Contact Email"
              type="email"
              placeholder="Email address"
              value={formData?.contact_email}
              onChange={(e) => handleInputChange('contact_email', e?.target?.value)}
              error={errors?.contact_email}
            />
          </div>

          {/* Amenities */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-foreground">Amenities</h3>
            <div className="grid grid-cols-3 gap-3">
              {commonAmenities?.map(amenity => (
                <label key={amenity} className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData?.amenities?.includes(amenity)}
                    onChange={() => handleAmenityToggle(amenity)}
                    className="rounded border-border"
                  />
                  <span className="text-sm text-foreground">{amenity}</span>
                </label>
              ))}
            </div>
          </div>
        </form>
      </div>

      <div className="p-6 border-t border-border">
        <div className="flex justify-end gap-3">
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={handleSubmit}
            loading={loading}
            iconName="Save"
          >
            {isEditing ? 'Update Venue' : 'Create Venue'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default VenueForm;